import React, { PureComponent, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { processedMonthlyExpenses } from './Services/reportExpense';

const data = [
  {
    name: 'January',
    currentYear: 4000,
    lastYear: 2400,
    amt: 2400,
  },
  {
    name: 'February',
    currentYear: 3000,
    lastYear: 1398,
    amt: 2210,
  },
  {
    name: 'March',
    currentYear: 2000,
    lastYear: 800,
    amt: 2290,
  },
  {
    name: 'April',
    currentYear: 2780,
    lastYear: 3908,
    amt: 2000,
  },
  {
    name: 'May',
    currentYear: 1890,
    lastYear: 4800,
    amt: 2181,
  },
  {
    name: 'June',
    currentYear: 2390,
    lastYear: 3800,
    amt: 2500,
  },
  {
    name: 'July',
    currentYear: 3490,
    lastYear: 4300,
    amt: 2100,
  },
  {
    name: 'August',
    currentYear: 1490,
    lastYear: 4300,
    amt: 2100,
  },
  {
    name: 'September',
    currentYear: 2490,
    lastYear: 1300,
    amt: 2100,
  },
  {
    name: 'October',
    currentYear: 3490,
    lastYear: 2300,
    amt: 2100,
  },
  {
    name: 'November',
    currentYear: 3490,
    lastYear: 1300,
    amt: 2100,
  },
  {
    name: 'December',
    currentYear: 2490,
    lastYear: 2300,
    amt: 2100,
  },
];

const ReportTotalExpenseComparisonGraph = ({ expenseData = [], expenseDataLoading, expenseDataError }) => {
  const reportingFinancialExpense = useSelector((state) => state.reportingExpense || {});
  const { forex } = useSelector((state) => state.forex || {});
  const settings = JSON.parse(localStorage.getItem("settings") || "{}");
  const setReportingCurrency = useSelector((state) => state.reportingExpense.expCurrency);
  var currencies = "USD";
  if (setReportingCurrency && setReportingCurrency.length === 1) {
    currencies = setReportingCurrency[0];
  } else {
    currencies = settings.currency;
  }

  const processedData = useMemo(() => {
    return processedMonthlyExpenses(expenseData, reportingFinancialExpense, settings, currencies, forex);
  }, [expenseData, reportingFinancialExpense, settings, forex]);

  const currentYear = reportingFinancialExpense.expYear || new Date().getFullYear();
  const previousYear = currentYear - 1;
  const isCurrentYear = currentYear === new Date().getFullYear();

  if (expenseDataLoading) {
    const skeletonHeight = 542;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    return (
      <div style={{ width: '100%', height: skeletonHeight }}>
        <div style={{ 
          width: '100%', 
          height: '100%', 
          padding: '20px 30px 5px 0px',
          position: 'relative',
          display: 'flex'
        }}>
          {/* X-axis skeleton labels (month names) - vertical on left */}
          <div style={{ 
            width: '120px',
            paddingRight: '10px',
            paddingLeft: '0px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingTop: '20px',
            paddingBottom: '5px',
            alignItems: 'flex-end'
          }}>
            {months.map((month, index) => (
              <div
                key={index}
                style={{
                  width: '100px',
                  height: '12px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>

          {/* Chart area with lines */}
          <div style={{ 
            flex: 1,
            position: 'relative',
            paddingTop: '20px',
            paddingBottom: '5px',
            paddingLeft: '10px',
            paddingRight: '30px'
          }}>
            {/* Grid lines skeleton - vertical */}
            <div style={{ 
              position: 'absolute',
              top: '20px',
              left: '10px',
              right: '30px',
              bottom: '5px',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              {Array.from({ length: 12 }).map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '1px',
                    height: '100%',
                    background: 'repeating-linear-gradient(to bottom, #D6D9DB 0px, #D6D9DB 3px, transparent 3px, transparent 6px)',
                    opacity: 0.3
                  }}
                />
              ))}
            </div>

            {/* Skeleton line paths - vertical */}
            <div style={{ 
              position: 'absolute',
              top: '20px',
              left: '10px',
              right: '30px',
              bottom: '5px',
              width: 'calc(100% - 40px)',
              height: 'calc(100% - 25px)'
            }}>
              {/* First line skeleton (Current Year - #96A8BA) */}
              <svg width="100%" height="100%" preserveAspectRatio="none" style={{ position: 'absolute', top: 0, left: 0 }}>
                <path
                  d="M 0,100 L 9.09,85 L 18.18,70 L 27.27,60 L 36.36,50 L 45.45,45 L 54.54,40 L 63.63,35 L 72.72,30 L 81.81,25 L 90.90,20 L 100,15"
                  stroke="#96A8BA"
                  strokeWidth="2"
                  fill="none"
                  opacity="0.3"
                  style={{ animation: 'pulse 1.5s ease-in-out infinite' }}
                />
              </svg>
              {/* Second line skeleton (Last Year - #6479F8) */}
              <svg width="100%" height="100%" preserveAspectRatio="none" style={{ position: 'absolute', top: 0, left: 0 }}>
                <path
                  d="M 0,90 L 9.09,75 L 18.18,65 L 27.27,55 L 36.36,50 L 45.45,45 L 54.54,40 L 63.63,35 L 72.72,30 L 81.81,25 L 90.90,20 L 100,18"
                  stroke="#6479F8"
                  strokeWidth="2"
                  fill="none"
                  opacity="0.3"
                  style={{ animation: 'pulse 1.5s ease-in-out infinite', animationDelay: '0.2s' }}
                />
              </svg>
            </div>

            {/* Y-axis skeleton labels (currency values) - horizontal at bottom */}
            <div style={{ 
              position: 'absolute',
              bottom: '5px',
              left: '10px',
              right: '30px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              paddingTop: '20px',
              width: 'calc(100% - 40px)'
            }}>
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '50px',
                    height: '12px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '4px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    flexShrink: 0
                  }}
                />
              ))}
            </div>
          </div>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }

  if (expenseDataError) return <div>Error loading data.</div>;

  return (
    <div style={{ width: '100%', height: 542 }}>
      <ResponsiveContainer>
        <LineChart
          layout="horizontal"
          data={processedData}
          margin={{
            top: 20,
            right: 30,
            left: Math.max(
              0, // minimum margin
              new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: currencies,
              })
                .format(Math.max(...processedData.map((item) => Math.max(item.currentYear, item.lastYear))))
                .length // scale it based on the label length
            ),
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" type="category" padding={{ left: 50 }} tickMargin={20} />
          <YAxis type="number" tickFormatter={(value) => new Intl.NumberFormat("en-US", { style: "currency", currency: currencies, }).format(value)} />
          <Tooltip formatter={(value) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: currencies, }).format(value)}`} />
          <Legend />
          <Line dataKey="currentYear" name={isCurrentYear ? `Current Year (${currentYear})` : `Selected Year (${currentYear})`} stroke="#96A8BA" />
          <Line dataKey="lastYear" name={isCurrentYear ? `Last Year (${previousYear})` : `Previous Year (${previousYear})`} stroke="#6479F8" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ReportTotalExpenseComparisonGraph;
